(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD
    define(['underscore', 'templates',
            'app/widget/main-nav/MainNavView',
            'app/behaviors/PreviewBehavior',
            'app/user/userService',
            'app/widget/modal/UseModalBehavior',
            'app/widget/modal/ModalDialogWidget',
            'app/applicationUtils',
            'app/financial/financialFeatureConfigModel',
            'app/appointment-config/appointmentConfigurationModel',
            'app/demographics/demographicsFeatureConfigModel',
            'app/user/userFeatureConfigModel',
            'app/branding-config/image/ImageReferenceDataBehavior'], factory);
  } else if (typeof exports === 'object') {
    // Node, CommonJS-like
    var templates = {
      navTemplate: require('./navTemplate.hbs')
    };
    module.exports = factory(require('underscore'), templates,
                             require('./../widget/main-nav/MainNavView'),
                             require('./../behaviors/PreviewBehavior'),
                             require('./../user/userService'),
                             require('./../widget/modal/UseModalBehavior'),
                             require('./../widget/modal/ModalDialogWidget'),
                             require('./../applicationUtils'),
                             require('./../financial/financialFeatureConfigModel'),
                             require('./../appointment-config/appointmentConfigurationModel'),
                             require('./../demographics/demographicsFeatureConfigModel'),
                             require('./../user/userFeatureConfigModel'),
                             require('./../branding-config/image/ImageReferenceDataBehavior'));
  } else {
    throw 'Only AMD and CommonJS imports are supported.';
  }
}(this, function (_, templates, MainNavView,
                  PreviewBehavior,
                  userService,
                  UseModalBehavior,
                  ModalDialogWidget,
                  applicationUtils,
                  financialFeatureConfigModel,
                  appointmentConfigurationModel,
                  demographicsFeatureConfigModel,
                  userFeatureConfigModel,
                  ImageReferenceDataBehavior) {
  'use strict';

  var HEADER_IMAGE_REFERENCE_NAME = 'logo-header';
  var HEADER_SECONDARY_IMAGE_REFERENCE_NAME = 'logo-tagline';

  var TOP_LEVEL_NAV_ITEMS = {
    NO_CURRENT: '',
    PATIENT_NAME: 'patient-name',
    DASHBOARD: 'dashboard',
    PROFILE: 'profile',
    MESSAGE: 'message',
    APPOINTMENT: 'appointment',
    FINANCIAL: 'financial',
    SETTINGS: 'settings'
  };

  /**
   * View for the main navigation on patient portal
   *
   * @class NavView
   */
  var NavView = MainNavView.extend({
    template: templates.navTemplate,
    TOP_LEVEL_NAV_ITEMS: TOP_LEVEL_NAV_ITEMS,

    events: _.extend({}, MainNavView.prototype.events, {
      'click [data-logout]' : '_logout'
    }),

    behaviors: _.extend({}, MainNavView.prototype.behaviors, {
      previewBehavior: PreviewBehavior,
      useModalBehavior: UseModalBehavior,
      headerLogoImageReferences: {
        behavior: ImageReferenceDataBehavior,
        ids: [HEADER_IMAGE_REFERENCE_NAME, HEADER_SECONDARY_IMAGE_REFERENCE_NAME]
      }
    }),

    prepareFields: {
      firstName: function() {
        return userService.currentUserModel.get('firstName');
      },
      patientVecnaId: function() {
        return userService.currentDemographicsModel.get('vecnaId');
      },
      unreadMessageCounts: function() {
        return userService.messageCountsModel.toJSON();
      },
      appointmentFeatureEnabled: function() {
        return appointmentConfigurationModel.get('enabled');
      },
      demographicsFeatureEnabled: function() {
        return demographicsFeatureConfigModel.isEnabled(demographicsFeatureConfigModel.CONFIG_LEVEL.NORMAL);
      },
      financialFeatureEnabled: function() {
        return financialFeatureConfigModel.isEnabled(financialFeatureConfigModel.CONFIG_LEVEL.NORMAL);
      },
      allPortalPaymentsEnabled: function() {
        return financialFeatureConfigModel.isAllPortalPaymentsEnabled(financialFeatureConfigModel.CONFIG_LEVEL.NORMAL);
      },
      identityProviderEnabled: function() {
        return userFeatureConfigModel.get('identityProviderEnabled');
      },
      TOP_LEVEL_NAV_ITEMS: TOP_LEVEL_NAV_ITEMS,
      currentApplication: applicationUtils.currentApplication,
      'logo-header': function() {
        return {
          isNotSet: this._isImageReferenceNotSet(HEADER_IMAGE_REFERENCE_NAME),
          isNone: this._isEmptyImageReference(HEADER_IMAGE_REFERENCE_NAME)
        };
      },
      'logo-tagline': function() {
        return {
          isNone: this._isEmptyImageReference(HEADER_SECONDARY_IMAGE_REFERENCE_NAME)
        };
      },
      currentNavItem: function() {
        var currentNavItem = this.get('currentNavItem');
        var currentDropdownItemParent = this.get('currentDropdownItemParent');
        return currentDropdownItemParent || currentNavItem;
      }
    },

    /**
     * @method initialize
     * @override
     */
    initialize: function() {
      MainNavView.prototype.initialize.apply(this);
      this.listenTo(userService.currentUserModel, 'change:firstName', this.render);
      this.listenTo(userService.currentDemographicsModel, 'change:vecnaId', this.render);
      this.listenTo(userService.messageCountsModel, 'change', this.render);

      this.listenTo(financialFeatureConfigModel, 'load-complete', this.render);
      financialFeatureConfigModel.loadOnce();

      this.listenTo(appointmentConfigurationModel, 'load-complete', this.render);
      appointmentConfigurationModel.loadOnce();

      this.listenTo(demographicsFeatureConfigModel, 'load-complete', this.render);
      demographicsFeatureConfigModel.loadOnce();

      this.listenTo(userFeatureConfigModel, 'load-complete', this.render);
      userFeatureConfigModel.loadOnce();
    },

    /**
     * Disable modals.  This is run by the behavior when displaying a preview of the nav and is meant to disable
     * all click-related functionality that navigates away from the preview.
     *
     * @method _disableForPreview
     * @private
     * @override
     */
    _disableForPreview: function() {
      this.set('disabledModals', true);
    },

    /**
     * Instantiates a confirmation popup for logout, and logs the user out if it returns true.
     * @method _logout
     * @param event {Object} the click event
     * @private
     */
    _logout: function(event) {
      event.preventDefault();
      this._modalDialogWidget = new ModalDialogWidget({
        dataViewName: 'confirm-portal-signout',
        descriptionKey: 'nav.signOutModal.body',
        yesKey: 'nav.signOutModal.signOut',
        noKey: 'nav.signOutModal.stay'
      });
      this.showModal(this._modalDialogWidget, function() {
        window.location.href = applicationUtils.currentApplication.logoutUrl;
      });
    },

    /**
     * @method _isEmptyImageReference
     * @param imageName {String} the name of the image to check if it has an empty reference.
     * @return {Boolean} true if the image configuration has an empty file name or the reference hasn't completed fetching.
     * @private
     */
    _isEmptyImageReference: function(imageName) {
      var imageReferenceDataBehavior = this.getBehavior('headerLogoImageReferences');
      var imageReference = imageReferenceDataBehavior.data.getModel(imageName);
      var imageReferenceFileName = imageReference && imageReference.get('fileName');
      var isEmptyImageReference = !imageReferenceFileName;
      if (this.isPreview()) {
        isEmptyImageReference = this.isEmptyImagePreview(imageName);
      }
      return isEmptyImageReference;
    },

    /**
     * @method _isImageReferenceNotSet
     * @param imageName {String} the name of the image to check if it has not been set.
     * @return {Boolean} true if the image configuration does not exist or the reference hasn't completed fetching.
     * @private
     */
    _isImageReferenceNotSet: function(imageName) {
      var imageReferenceDataBehavior = this.getBehavior('headerLogoImageReferences');
      var imageReference = imageReferenceDataBehavior.data.getModel(imageName);
      var imageReferenceNotSet = _.isUndefined(imageReference);
      if (this.isPreview()) {
        imageReferenceNotSet = this.isImagePreviewNotSet(imageName);
      }
      return imageReferenceNotSet;
    }
  });

  NavView.TOP_LEVEL_NAV_ITEMS = TOP_LEVEL_NAV_ITEMS;

  return NavView;
}));
